import { createContext, use, useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { getPublicEnv } from "#/modules/util.instrumentation/public-env";
import { z } from "zod/v4";

export type Theme = "light" | "dark" | "system";

const THEME_COOKIE = "bb-theme";
const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const THEME_PATTERN = /(?:^|; )bb-theme=(dark|light)/;

/* Soft-launch gate while the dark theme gets visual polish: always on in development, and
   unlockable anywhere by visiting any page with `?dark` in the URL — a one-way, per-browser
   opt-in (year-long cookie, no way back). The unlock happens pre-paint in themeInitScript.
   Remove the gate entirely to roll out. */
const UNLOCK_COOKIE = "bb-theme-unlocked";
const UNLOCK_PATTERN = /(?:^|; )bb-theme-unlocked=1/;

function isDev() {
  return getPublicEnv().ASPNETCORE_ENVIRONMENT === "development";
}

export function isDarkModeEnabled() {
  return isDev() || (!import.meta.env.SSR && UNLOCK_PATTERN.test(document.cookie));
}

/* Classic head script: handles the `?dark` unlock, then applies the cookie'd theme while the
   document parses, before first paint, so a dark-mode reload never flashes light. Must stay in
   sync with the logic below. Always injected (routes/__root.tsx); the dev flag is baked in
   server-side per environment. */
export function getThemeInitScript() {
  return `(function(){try{if(new URLSearchParams(location.search).has("dark"))document.cookie="${UNLOCK_COOKIE}=1;path=/;max-age=${THEME_COOKIE_MAX_AGE};samesite=lax";if(!${isDev()}&&!/${UNLOCK_PATTERN.source}/.test(document.cookie))return;var m=document.cookie.match(/${THEME_PATTERN.source}/);document.documentElement.classList.toggle("dark",m?m[1]==="dark":matchMedia("(prefers-color-scheme: dark)").matches)}catch(e){}})();`;
}

const themeSchema = z.enum(["light", "dark", "system"]).catch("system");

function readThemeCookie(): Theme {
  if (import.meta.env.SSR) return "system";
  return themeSchema.parse(document.cookie.match(THEME_PATTERN)?.[1]);
}

function systemPrefersDark() {
  return matchMedia("(prefers-color-scheme: dark)").matches;
}

const ThemeContext = createContext<{ enabled: boolean; isDark: boolean; toggleDark: () => void }>({
  enabled: false,
  isDark: false,
  toggleDark: () => {},
});

/* Two visible states, three underlying (lea.verou.me/blog/2026/dark-mode-toggles): no cookie
   means "follow the OS". Toggling to what the OS already resolves clears the override instead
   of storing a matching value — evaluated only at interaction time, so a later OS change never
   silently rewrites an explicit choice. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  // Hydration-safe gate: the cookie half of isDarkModeEnabled() only exists in the browser, so
  // start from the env half (identical on server and client) and pick up an unlock post-mount.
  const [enabled, setEnabled] = useState(isDev);
  useEffect(() => {
    if (isDarkModeEnabled()) setEnabled(true);
  }, []);
  const [theme, setTheme] = useState<Theme>(readThemeCookie);
  const [systemDark, setSystemDark] = useState(() => !import.meta.env.SSR && systemPrefersDark());

  const isDark = enabled && (theme === "dark" || (theme === "system" && systemDark));

  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.toggle("dark", isDark);
  }, [enabled, isDark]);

  useEffect(() => {
    if (!enabled) return;
    const query = matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [enabled]);

  const toggleDark = useCallback(() => {
    const currentSystemDark = systemPrefersDark();
    const nextDark = !(theme === "dark" || (theme === "system" && currentSystemDark));
    const next: Theme = nextDark === currentSystemDark ? "system" : nextDark ? "dark" : "light";
    document.cookie =
      next === "system"
        ? `${THEME_COOKIE}=;path=/;max-age=0`
        : `${THEME_COOKIE}=${next};path=/;max-age=${THEME_COOKIE_MAX_AGE};samesite=lax`;
    setSystemDark(currentSystemDark);
    setTheme(next);
  }, [theme]);

  return <ThemeContext value={{ enabled, isDark, toggleDark }}>{children}</ThemeContext>;
}

export function useTheme() {
  return use(ThemeContext);
}
